import React, { useEffect, useMemo, useState } from 'react';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import { RegistrationTeamStatus } from "@vote4dance/judging-types";
import { App, Button, Checkbox, Divider, Empty, List, Select, Skeleton, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { _T } from "../../../../../base-react/src/Localization";
var Api = apis.Api;
var Stream = hooks.streams.Stream;
var ClassesSource = models.classes.ClassesSource;
var DanceTeamUsersSource = models.danceteams.DanceTeamUsersSource;
var CompetitionRegistrationSource = models.registration.CompetitionRegistrationSource;
var RegistrationLogSource = models.registration.RegistrationLogSource;
var UserItem = models.users.UserItem;
var OrganizationSource = models.organizations.OrganizationSource;
var CompetitionSource = models.competitions.CompetitionsSource;
var DanceTeamUserItem = models.danceteams.DanceTeamUserItem;
import { CheckOutlined, DownloadOutlined, EditOutlined, MailOutlined } from '@ant-design/icons';
import { DanceTeamUserNames } from '../../../../../base-react/components/DanceTeamUserNames';
import { ImportTeamsModal } from '../../data/components/ImportTeams';
import { useCompetition } from '../../../../../base-react/hooks/Competition';
import { RegistrationTeamEdit } from './RegistrationTeamEdit';
import { PaymentInfo } from './PaymentInfo';
import { TeamImagePreviewButton } from '../../../../../base-react/components/TeamImagePreviewButton';
import dayjs from 'dayjs';
import { EmailParticipantsModal } from './EmailParticipants';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
const { Text } = Typography;
export const RegistrationList = (props) => {
    var _a, _b, _c;
    const { competition_id, classId, statuses, disableImport = false } = props;
    const [inputCompetition, event, , , , canEdit] = useCompetition(competition_id);
    const [registrations, loadingRegistrations] = Stream(CompetitionRegistrationSource(competition_id));
    const [organizations, loadingOrganizations] = Stream(OrganizationSource);
    const [classes, loadingClasses] = Stream(ClassesSource(competition_id));
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loadingRowKey, setLoadingRowKey] = useState();
    const [errorRowKeys, setErrorRowKeys] = useState([]);
    const [importClasses, setImportClasses] = useState([]);
    const [importTeams, setImportTeams] = useState([]);
    const [importCompare, setImportCompare] = useState();
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [sendEmailOnChange, setSendEmailOnChange] = useState(true);
    const { notification, message } = App.useApp();
    const [editItem, setEditItem] = useState(null);
    // --- Controlled table filters
    const [filteredInfo, setFilteredInfo] = useState({
        registration_status: (statuses && statuses.length > 0 ? statuses : [RegistrationTeamStatus.Created]),
        class_id: classId ? [classId] : undefined
    });
    const stopExpand = (e) => e.stopPropagation();
    // Track if user has touched filters; prevents future prop changes from overriding
    const [userChangedFilters, setUserChangedFilters] = useState(false);
    const [userCache, setUserCache] = useState({});
    const [userLoading, setUserLoading] = useState({});
    useEffect(() => {
        setLoadingRowKey(undefined);
    }, [registrations]);
    // If parent updates `statuses`, apply only if user hasn't changed filters yet
    useEffect(() => {
        if (!userChangedFilters) {
            setFilteredInfo((prev) => ({
                ...prev,
                registration_status: (statuses && statuses.length > 0) ? statuses : prev.registration_status
            }));
        }
    }, [statuses, userChangedFilters]);
    const handleTableChange = (_pagination, filters) => {
        setUserChangedFilters(true);
        setFilteredInfo(filters);
    };
    const uniqueClassIds = useMemo(() => {
        const set = new Set();
        (registrations || []).forEach(r => set.add(r.class_id));
        return Array.from(set);
    }, [registrations]);
    const usesPayment = useMemo(() => (registrations || []).some(a => a.registration_payment_done !== undefined && a.registration_payment_done !== null), [registrations]);
    // Active filters (respect clearing via nullish coalescing)
    const activeClassFilter = (_a = filteredInfo.class_id) !== null && _a !== void 0 ? _a : (classId ? [classId] : undefined);
    const activeStatusFilter = (_b = filteredInfo.registration_status) !== null && _b !== void 0 ? _b : undefined;
    const activePaymentFilter = filteredInfo.registration_payment_done
        ? filteredInfo.registration_payment_done.map((v) => Number(v))
        : undefined;
    // Filter by class/status/payment first; imported depends on what's visible
    const filteredDataBase = useMemo(() => {
        if (!registrations)
            return registrations;
        return registrations.filter(r => {
            const matchClass = activeClassFilter ? activeClassFilter.includes(r.class_id) : true;
            const matchStatus = activeStatusFilter ? activeStatusFilter.includes(r.registration_status) : true;
            const paymentKey = normalizePaid(r.registration_payment_done);
            const matchPayment = activePaymentFilter ? activePaymentFilter.includes(paymentKey) : true;
            return matchClass && matchStatus && matchPayment;
        });
    }, [registrations, activeClassFilter, activeStatusFilter, activePaymentFilter]);
    const usesImported = useMemo(() => (filteredDataBase || []).some(a => { var _a; return Number((_a = a.team_imported) !== null && _a !== void 0 ? _a : 0) > 0; }), [filteredDataBase]);
    const activeImportedFilter = usesImported ? ((_c = filteredInfo.team_imported) !== null && _c !== void 0 ? _c : undefined) : undefined;
    const filteredData = useMemo(() => {
        if (!filteredDataBase)
            return filteredDataBase;
        return filteredDataBase.filter(r => {
            var _a;
            const matchImported = activeImportedFilter ? activeImportedFilter.includes(Number((_a = r.team_imported) !== null && _a !== void 0 ? _a : 0)) : true;
            return matchImported;
        });
    }, [filteredDataBase, activeImportedFilter]);
    const statusOptions = Object.values(RegistrationTeamStatus).map(a => ({
        key: a,
        label: _T('registration.status.' + a),
        value: a
    }));
    const columns = useMemo(() => {
        var _a, _b, _c, _d;
        const cols = [
            {
                dataIndex: "team_name",
                title: _T('team.details.team_name.label'),
                render: (_text, item) => {
                    const defaultName = React.createElement(DanceTeamUserNames, { item: item });
                    const regJson = item.registration_data && safeParseJson(item.registration_data);
                    const displayName = (regJson === null || regJson === void 0 ? void 0 : regJson.team_name) || item.team_name;
                    if (!displayName) {
                        return defaultName;
                    }
                    return (React.createElement(Tooltip, { title: defaultName },
                        React.createElement(Text, { type: "warning" }, displayName)));
                },
                ellipsis: true
            }
        ];
        if (!classId) {
            cols.push({
                dataIndex: "class_id",
                title: _T('round.details.class_label.label'),
                filters: classes && classes
                    .filter(a => uniqueClassIds.includes(a.class_id))
                    .map(a => ({ text: a.class_label || a.class_name, value: a.class_id })),
                render: (id) => {
                    if (loadingClasses)
                        return React.createElement(Spin, null);
                    const foundClass = classes === null || classes === void 0 ? void 0 : classes.find(a => a.class_id === id);
                    return foundClass && (foundClass.class_label || foundClass.class_name);
                },
                filteredValue: (_a = filteredInfo.class_id) !== null && _a !== void 0 ? _a : null,
                onFilter: (value, record) => record.class_id == value,
                sorter: (a, b) => b.class_id.localeCompare(a.class_id),
                ellipsis: true
            });
        }
        cols.push(usesImported ? {
            dataIndex: "team_imported",
            title: _T('registration.import.yes'),
            ellipsis: true,
            render: (text) => text > 0 && React.createElement(CheckOutlined, null),
            filters: [0, 1].map(value => ({
                text: _T('registration.import.' + (value > 0 ? 'yes' : 'no')),
                value
            })),
            filteredValue: (_b = filteredInfo.team_imported) !== null && _b !== void 0 ? _b : null,
            onFilter: (value, record) => { var _a; return Number((_a = record.team_imported) !== null && _a !== void 0 ? _a : 0) == Number(value); },
            sorter: (a, b) => { var _a, _b; return ((_a = b.team_imported) !== null && _a !== void 0 ? _a : 0) - ((_b = a.team_imported) !== null && _b !== void 0 ? _b : 0); },
            responsive: ['md'],
        } : {}, {
            dataIndex: "registration_created_at",
            title: _T('common.date'),
            render: (text) => dayjs(text).format('LLL'),
            sorter: (a, b) => b.registration_created_at.localeCompare(a.registration_created_at),
            responsive: ['lg'],
            ellipsis: true
        }, {
            dataIndex: "registration_status",
            title: _T('common.status'),
            render: (text, item) => (React.createElement("div", { onClick: stopExpand, onMouseDown: stopExpand },
                React.createElement(Select, { placeholder: _T('common.select', { name: _T('class.title').toLowerCase() }), style: { width: '100%', minWidth: 0, whiteSpace: 'nowrap' }, onChange: status => statusSelectedTeams(item, status), loading: loadingRowKey === item.dance_team_id + '.' + item.class_id, disabled: !canEdit || (loadingRowKey === item.dance_team_id + '.' + item.class_id), value: text, status: errorRowKeys.includes(item.dance_team_id + '.' + item.class_id) ? "error" : undefined, options: statusOptions.map(opt => ({ ...opt, disabled: opt.value === text })) }))),
            filters: statusOptions.map(({ label, value }) => ({ text: label, value })),
            // IMPORTANT: controlled by state only (props.statuses is just initial)
            filteredValue: (_c = filteredInfo.registration_status) !== null && _c !== void 0 ? _c : null,
            onFilter: (value, record) => record.registration_status == value,
            sorter: (a, b) => b.registration_status.localeCompare(a.registration_status),
            responsive: ['sm']
        }, usesPayment ? {
            title: _T('registration.payment'),
            dataIndex: 'registration_payment_done',
            ellipsis: true,
            render: (value) => {
                const paid = Number(value) > 0;
                return (React.createElement(Text, { ellipsis: true, type: paid ? 'success' : 'secondary' }, paid ? _T('common.yes') : _T('common.no')));
            },
            filters: [
                { text: _T('common.yes'), value: 1 },
                { text: _T('common.no'), value: 0 },
            ],
            filteredValue: (_d = filteredInfo.registration_payment_done) !== null && _d !== void 0 ? _d : null,
            onFilter: (value, record) => normalizePaid(record.registration_payment_done) === Number(value),
            sorter: (a, b) => { var _a, _b; return Number((_a = b.registration_payment_done) !== null && _a !== void 0 ? _a : 0) - Number((_b = a.registration_payment_done) !== null && _b !== void 0 ? _b : 0); },
            responsive: ['md']
        } : {}, {
            key: 'actions',
            width: 50,
            render: (_, item) => (React.createElement(Space, { onClick: stopExpand, onMouseDown: stopExpand },
                React.createElement(Button, { onClick: () => setEditItem(item), disabled: !canEdit || selectedRowKeys.length > 0, icon: React.createElement(EditOutlined, null) })))
        });
        return cols.filter((c) => Object.keys(c).length > 0);
    }, [
        _T, canEdit, classId, classes,
        event === null || event === void 0 ? void 0 : event.competition_id,
        filteredInfo.class_id, filteredInfo.registration_payment_done, filteredInfo.registration_status, filteredInfo.team_imported,
        loadingClasses, loadingRowKey, selectedRowKeys.length, statusOptions, usesPayment, usesImported, competition_id,
        errorRowKeys, uniqueClassIds
    ]);
    const fetchUsersForTeam = async (teamId) => {
        if (userCache[teamId])
            return;
        setUserLoading(prev => ({ ...prev, [teamId]: true }));
        try {
            const users = await Api.read(DanceTeamUsersSource(teamId).url);
            setUserCache(prev => ({ ...prev, [teamId]: users || [] }));
        }
        catch {
            setUserCache(prev => ({ ...prev, [teamId]: [] }));
        }
        finally {
            setUserLoading(prev => ({ ...prev, [teamId]: false }));
        }
    };
    const expandedRowRender = (record) => {
        var _a;
        const users = userCache[record.dance_team_id] || [];
        const loading = userLoading[record.dance_team_id];
        const regData = record.registration_data && safeParseJson(record.registration_data);
        const registrationImageId = Array.isArray(regData)
            ? (_a = regData.find((item) => (item === null || item === void 0 ? void 0 : item.label) === 'team_image_id')) === null || _a === void 0 ? void 0 : _a.value
            : regData === null || regData === void 0 ? void 0 : regData.team_image_id;
        return (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
            loading && React.createElement(Spin, null),
            !loading && users.length === 0 && React.createElement(Text, { type: "secondary" }, _T('common.empty')),
            !loading && users.length > 0 && users.map((user, index) => {
                const age = user.user_birthdate ? dayjs().diff(dayjs(user.user_birthdate), 'year') : null;
                const birth = user.user_birthdate ? dayjs(user.user_birthdate) : null;
                return (React.createElement(Space, { key: user.user_id, separator: React.createElement(Divider, { orientation: "vertical" }), wrap: true },
                    React.createElement("strong", null, DanceTeamUserItem.getFullName(user)),
                    user.dance_role && _T('danceteam.details.dance_role.' + user.dance_role, {}, user.dance_role),
                    age !== null && age,
                    birth && birth.format('YYYY-MM-DD'),
                    user.user_country,
                    user.organization_tag || user.organization_name,
                    index === 0 && React.createElement(TeamImagePreviewButton, { imageId: registrationImageId })));
            }),
            regData && (React.createElement(Space, { orientation: "vertical" },
                !Array.isArray(regData) && Object.entries(regData).map(([key, value]) => {
                    if (key === 'form')
                        return null;
                    if (key === 'team_image_id')
                        return null;
                    if (value === undefined || value === null || value === '')
                        return null;
                    return (React.createElement("div", { key: key },
                        React.createElement("strong", null,
                            key,
                            ":"),
                        " ",
                        String(value)));
                }),
                Array.isArray(regData) && regData.map((item, index) => ((item === null || item === void 0 ? void 0 : item.label) === 'team_image_id' ? null : (React.createElement("div", { key: index },
                    React.createElement("strong", null,
                        item.label,
                        ":"),
                    " ",
                    item.value)))))),
            React.createElement(RegistrationLogInfo, { competition_id: competition_id, class_id: record.class_id, dance_team_id: record.dance_team_id }),
            usesPayment && record.registration_payment_ref && (React.createElement(PaymentInfo, { competitionId: competition_id, paymentRef: record.registration_payment_ref }))));
    };
    const updateAllParticipants = async () => {
        var _a, _b, _c, _d, _e;
        if (!filteredData)
            return;
        const teamList = [];
        const classList = [];
        const validRegistrations = filteredData.filter(a => [RegistrationTeamStatus.Created, RegistrationTeamStatus.Signed, RegistrationTeamStatus.Approved].includes(a.registration_status));
        for (const registration of validRegistrations) {
            const danceTeamId = registration.dance_team_id;
            const classIdX = registration.class_id;
            const danceTeam = registration;
            const foundClass = classes === null || classes === void 0 ? void 0 : classes.find(a => a.class_id === classIdX);
            classList.push(foundClass ? foundClass : {
                class_name: classIdX,
                class_label: classIdX
            });
            const foundOrganization = danceTeam.organization_id ? organizations === null || organizations === void 0 ? void 0 : organizations.find(a => a.organization_id === danceTeam.organization_id) : undefined;
            const users = await Api.read(DanceTeamUsersSource(danceTeamId).url);
            const orgShort = (users === null || users === void 0 ? void 0 : users.length) > 0 ? users.map(a => a.organization_tag).filter(Boolean).join('/') : foundOrganization === null || foundOrganization === void 0 ? void 0 : foundOrganization.organization_tag;
            const orgFull = (users === null || users === void 0 ? void 0 : users.length) > 0 ? users.map(a => a.organization_name).filter(Boolean).join('/') : foundOrganization === null || foundOrganization === void 0 ? void 0 : foundOrganization.organization_name;
            const country = (users === null || users === void 0 ? void 0 : users.length) > 0 ? users[0].user_country : foundOrganization === null || foundOrganization === void 0 ? void 0 : foundOrganization.organization_country;
            const regJson = danceTeam.registration_data && safeParseJson(danceTeam.registration_data);
            const formTeamName = regJson && Array.isArray(regJson)
                ? (_a = regJson.find((f) => (f === null || f === void 0 ? void 0 : f.label) === 'team_name')) === null || _a === void 0 ? void 0 : _a.value
                : undefined;
            const regTeamName = (_b = regJson === null || regJson === void 0 ? void 0 : regJson.team_name) !== null && _b !== void 0 ? _b : formTeamName;
            let newTeam = Object.assign({
                team_name_1: users[0] && DanceTeamUserItem.getFullName(users[0]),
                team_name_2: users[1] && DanceTeamUserItem.getFullName(users[1]),
                team_name_3: users[2] && DanceTeamUserItem.getFullName(users[2]),
                team_name: regTeamName !== null && regTeamName !== void 0 ? regTeamName : danceTeam.team_name,
                team_org: orgShort,
                team_org_full: orgFull,
                team_class: foundClass ? foundClass.class_name : classIdX,
                team_org_1: users[0] && users[0].organization_tag,
                team_org_2: users[1] && users[1].organization_tag,
                team_org_3: users[2] && users[2].organization_tag,
                team_year_1: ((_c = users[0]) === null || _c === void 0 ? void 0 : _c.user_birthdate) ? dayjs(users[0].user_birthdate).format('YYYY') : undefined,
                team_year_2: ((_d = users[1]) === null || _d === void 0 ? void 0 : _d.user_birthdate) ? dayjs(users[1].user_birthdate).format('YYYY') : undefined,
                team_year_3: ((_e = users[2]) === null || _e === void 0 ? void 0 : _e.user_birthdate) ? dayjs(users[2].user_birthdate).format('YYYY') : undefined,
                team_person_id_1: users[0] && DanceTeamUserItem.getExternalForFederation(users[0], inputCompetition === null || inputCompetition === void 0 ? void 0 : inputCompetition.federation_id),
                team_person_id_2: users[1] && DanceTeamUserItem.getExternalForFederation(users[1], inputCompetition === null || inputCompetition === void 0 ? void 0 : inputCompetition.federation_id),
                team_person_id_3: users[2] && DanceTeamUserItem.getExternalForFederation(users[2], inputCompetition === null || inputCompetition === void 0 ? void 0 : inputCompetition.federation_id),
                team_country: country,
                dance_team_id: danceTeamId
            }, regJson);
            const splitSource = regTeamName !== null && regTeamName !== void 0 ? regTeamName : danceTeam.team_name;
            if (splitSource) {
                const nameParts = splitSource.split('&');
                newTeam.team_name_1 = nameParts[0];
                newTeam.team_name_2 = nameParts[1];
                newTeam.team_name_3 = nameParts[2];
            }
            if (regJson && Array.isArray(regJson)) {
                regJson.forEach((f) => {
                    if (typeof f.label === 'string' && f.label.startsWith('team_')) {
                        newTeam[f.label] = f.value;
                    }
                });
            }
            teamList.push(newTeam);
        }
        setImportClasses(classList);
        setImportTeams(teamList);
        importTeamsAPI(true, null, classList, teamList);
    };
    const importTeamsAPI = (preview, checked, classesArg, teamsArg) => {
        setIsLoading(true);
        Api.call(`${CompetitionSource.url}/${competition_id}`, 'import-teams', {
            classes: classesArg || importClasses,
            teams: teamsArg || importTeams,
            preview: preview,
            checked: checked
        }).then((response) => {
            setImportCompare(response);
            setOpen(true);
            setIsLoading(false);
            if (!preview) {
                notification.success({ message: _T('data.import.success') });
            }
        }).catch((err) => {
            message.error(err.message);
            setIsLoading(false);
        });
    };
    const statusSelectedTeams = async (item, status) => {
        let updateKeys = [...selectedRowKeys];
        if (selectedRowKeys.length === 0) {
            updateKeys = [item.dance_team_id + '.' + item.class_id];
        }
        setErrorRowKeys([]);
        for (const key of updateKeys) {
            setLoadingRowKey(key);
            const [danceTeamId, classIdX] = key.toString().split('.');
            await Api.call(`${CompetitionRegistrationSource(competition_id).url}`, 'status', {
                dance_team_id: danceTeamId,
                class_id: classIdX,
                status: status,
                send: sendEmailOnChange
            })
                .then(() => {
                notification.success({
                    message: _T('common.apply.success', { name: _T('registration.title').toLowerCase() })
                });
            })
                .catch((err) => {
                setErrorRowKeys(keys => [...keys, key]);
                setLoadingRowKey(undefined);
                notification.error({
                    message: err.response && err.response.message ? _T(err.response.message) : err.message
                });
            });
        }
    };
    const emailParticipants = async (msg) => {
        // selectedRowKeys are "dance_team_id.class_id"
        const list = selectedRowKeys.map(key => {
            const [dance_team_id, class_id] = String(key).split('.');
            return { dance_team_id, class_id };
        });
        await Api.call(`${CompetitionRegistrationSource(competition_id).url}`, 'email', { message: msg, list })
            .then(() => notification.success({ message: _T('registration.contact.success') }))
            .catch((err) => notification.error({
            message: err.response && err.response.message ? _T(err.response.message) : err.message
        }));
    };
    const footer = () => (React.createElement(Space, { wrap: true },
        !disableImport &&
            React.createElement(Button, { type: "primary", loading: isLoading || loadingOrganizations, icon: React.createElement(DownloadOutlined, null), onClick: updateAllParticipants, disabled: !canEdit || selectedRowKeys.length > 0 }, _T('registration.import.title')),
        React.createElement(EmailParticipantsModal, { open: showEmail, onEmail: m => emailParticipants(m), onClose: () => setShowEmail(false) }),
        React.createElement(Button, { icon: React.createElement(MailOutlined, null), onClick: () => setShowEmail(true), disabled: !canEdit || selectedRowKeys.length === 0 }, _T('registration.contact.title')),
        React.createElement(Checkbox, { checked: sendEmailOnChange, onChange: e => setSendEmailOnChange(e.target.checked) }, _T('registration.contact.email'))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Table, { rowSelection: { onChange: (keys) => setSelectedRowKeys(keys) }, columns: columns, dataSource: filteredData, loading: loadingRegistrations, pagination: false, onChange: handleTableChange, size: "small", rowKey: (record) => record.dance_team_id + '.' + record.class_id, expandable: {
                expandedRowRender,
                onExpand: (expanded, record) => {
                    if (expanded) {
                        void fetchUsersForTeam(record.dance_team_id);
                    }
                },
                expandRowByClick: true
            }, footer: footer }),
        editItem && (React.createElement(RegistrationTeamEdit, { key: String(editItem.dance_team_id) + '.' + String(editItem.class_id), item: editItem, event_id: event === null || event === void 0 ? void 0 : event.competition_id, competition_id: competition_id, registration_period_id: editItem.registration_period_id, disabled: !canEdit || selectedRowKeys.length > 0, openOnMount: true, hideTriggerButton: true, onClose: () => setEditItem(null) })),
        React.createElement(ImportTeamsModal, { open: open, competition_id: competition_id, importCompare: importCompare, onImport: (checked) => importTeamsAPI(false, checked), onClose: () => { setImportCompare(undefined); setOpen(false); } })));
};
// ---------- Helpers & subcomponents ----------
const safeParseJson = (text) => {
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
};
const normalizePaid = (v) => Number(v) > 0 ? 1 : 0;
const RegistrationLogInfo = (props) => {
    var _a;
    const { competition_id, class_id, dance_team_id } = props;
    const [log, loading] = Stream(RegistrationLogSource(competition_id, class_id, dance_team_id));
    const [showAll, setShowAll] = useState(false);
    useEffect(() => {
        setShowAll(false);
    }, [log]);
    if (!log)
        return React.createElement(Empty, null);
    const visibleLog = showAll ? (log || []) : (log || []).slice(0, 2);
    const hasMore = ((_a = log === null || log === void 0 ? void 0 : log.length) !== null && _a !== void 0 ? _a : 0) > 2;
    return (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
        React.createElement(List, { size: "small", bordered: true, dataSource: visibleLog, renderItem: (item) => (React.createElement(List.Item, null,
                React.createElement(Skeleton, { loading: loading, active: true },
                    React.createElement(Space, { orientation: "vertical" },
                        React.createElement("div", null,
                            React.createElement("strong", null,
                                item.registration_log_trigger,
                                ":"),
                            " ",
                            dayjs(item.registration_log_ts).format('LLL')),
                        React.createElement("div", null, UserItem.getFullName(item)),
                        item.registration_log_data && Object.entries(safeParseJson(item.registration_log_data) || {}).map(([key, value]) => (React.createElement("div", { key: key },
                            React.createElement("strong", null,
                                key,
                                ":"),
                            " ",
                            String(value)))))))) }),
        hasMore && (React.createElement(Button, { type: "link", size: "small", onClick: () => setShowAll(prev => !prev) }, showAll ? _T('common.showLess') : _T('common.showMore')))));
};
